/**
 * @file IntersectionObserver Manager
 * @author quda
 */

class IntersectionObserverManager {
    constructor() {
        this.ioMap = new Map();
    }

    /**
     * 生成缓存键
     */
    genCacheKey(config = {}) {
        const {root, ...rest} = config;
        const rootName = root ? (root.id || '_default') : 'root_view'; // 默认顶级文档的视口

        if (root && !root.id) {
            console.warn('[IntersectionObserverManager] 请为观测根节点设置唯一ID，否则观测行为可能不符合预期');
        }

        return `${rootName}-${JSON.stringify(rest)}`;

    }

    /**
     * 根据配置获取IntersectionObserver实例，没有则自动创建
     *
     * @param config IntersectionObserver配置对象
     * @returns 返回IntersectionObserver实例
     */
    getIOInstance(config = {}) {
        const cacheKey = this.genCacheKey(config);
        if (this.ioMap.has(cacheKey)) {
            return this.ioMap.get(cacheKey);
        }

        const io = new IntersectionObserver((entries, observer) => {
            entries.forEach(entry => this.handleIntersect(entry, observer));
        }, config);
        io._observedElements = new WeakMap(); // 当前实例观察的元素集合

        this.ioMap.set(cacheKey, io);

        return io;
    }

    /**
     * 处理交叉观察事件
     *
     * @param entry IntersectionObserverEntry
     * @param observer 交叉观察器实例
     */
    handleIntersect(entry, observer) {
        const targetInfo = observer._observedElements.get(entry.target);
        const {callback, maxTime, ratio} = targetInfo;

        if (!(entry.isIntersecting && entry.intersectionRatio >= ratio)) {
            return;
        }

        let {times = 0} = targetInfo;
        if (times >= maxTime) {
            observer.unobserve(entry.target);
            observer._observedElements.delete(entry.target);
            return;
        }

        if (callback && typeof callback === 'function') {
            callback(entry, observer);
        }

        times++;
        observer._observedElements.set(entry.target, {...targetInfo, times});
    }

    /**
     * 添加观察元素
     * @param {HTMLElement} target 目标元素
     * @param {Function} callback 回调函数
     * @param {Object} config IntersectionObserver配置
     * @param {=Number} config.threshold 阈值
     * @param {=HTMLElement} config.root 边界元素
     * @param {=Boolean} config.rootMargin 是否使用根元素作为边界
     * @param {Object} options 观察策略，控制交叉观察回调命中条件
     * @param {=Number} options.ratio 交叉阈值比率，0 ~ 1, 默认0
     * @param {=Number} options.maxTime 最大观察次数，命中回调即为一次，默认1
     */
    observe(target, callback, observeConfig = {}, options = {}) {
        // 校验当前global环境中是否包含IntersectionObserver
        if (!window.IntersectionObserver) {
            console.warn('[IntersectionObserverManager] 当前浏览器不支持IntersectionObserver API');
            return;
        }

        if (typeof callback !== 'function') {
            console.warn('[IntersectionObserverManager] observe回调函数必须为Function类型');
            return;
        }

        const io = this.getIOInstance(observeConfig);
        const targets = io._observedElements;

        if (!targets.has(target)) {
            io.observe(target);
            const {maxTime = 1, ratio = 0} = options;
            targets.set(target, {
                callback,
                ratio,
                maxTime
            });
        }
    }

    /**
     * 清除全部监听
     */
    clear() {
        for (const io of this.ioMap.values()) {
            io.disconnect();
        }

        this.ioMap.clear();
        this.ioMap = null;
    }
}

export default IntersectionObserverManager;